/**
 * QA-1689 F2 (22 Oct 2026), the screen's half of the STOP path's envelope revision (api/src/lib/optout-everywhere.ts).
 * A STOP recorded after a case's resolution envelope left is sent to the Portal on a revision of that envelope. When
 * that revision cannot be written the approval stands, the STOP is on the member's file, and the case says so in a
 * note (data.kind 'optout-everywhere-envelope', reemitted false). The drawer:
 *   1. says the failure plainly after Approve (the answer's optOutEverywhere.envelope.reemitted is false), never success
 *      alone;
 *   2. offers Approve (retry) on a cleared case whose latest envelope note says the STOP was not sent. The server sends
 *      it on Approve again (api/src/care/event-review.ts retryLateStop) and approves nothing new.
 * The server decides; this file only reads what it wrote. NO em dashes.
 */
import type { EventAction } from '@lib/care-model';

/** The case note's data.kind (api/src/lib/optout-everywhere.ts OPTOUT_EVERYWHERE_ENVELOPE_NOTE). */
export const STOP_ENVELOPE_NOTE = 'optout-everywhere-envelope';

export const STOP_ENVELOPE_WORDING = {
  head: 'STOP not sent to the Portal',
  failed: 'The STOP was NOT sent to the Portal. The approval stands and the STOP is on the member\'s file, but the Portal has not received it. Press Approve (retry) to send it.',
  sent: 'The STOP was sent to the Portal.',
  retry: 'Approve (retry)',
  retrying: 'Sending...',
} as const;

/** True when an Approve answer says the STOP's envelope revision could not be written. */
export function stopEnvelopeFailed(answer: unknown): boolean {
  const env = (answer as { optOutEverywhere?: { envelope?: { reemitted?: unknown } } } | null | undefined)?.optOutEverywhere?.envelope;
  return !!env && env.reemitted === false;
}

/** True for a cleared case whose latest envelope note says its STOP has not reached the Portal yet. */
export function stopEnvelopeMissing(ev: { status?: string; actions?: ReadonlyArray<Pick<EventAction, 'type' | 'data'>> | null } | null | undefined): boolean {
  if (!ev || ev.status !== 'cleared') return false;
  const last = [...(ev.actions ?? [])].reverse()
    .find((a) => (a.type as string) === 'note' &&(a.data as Record<string, unknown> | undefined)?.['kind'] === STOP_ENVELOPE_NOTE);
  return !!last && (last.data as Record<string, unknown>)['reemitted'] === false;
}
